import { queryKeys } from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { client, jsonOrThrow } from "../api/healthos";
import { useDocumentTitle } from "../hooks/use-document-title";
import { ContactCentreChrome } from "./contact-centre-chrome";

export function useContactCentreMe(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.contactCentreMe,
    enabled: options?.enabled ?? true,
    retry: 3,
    queryFn: async () => {
      const response = await client().api["contact-centre"].me.$get();
      if (response.status === 403) return null;
      return jsonOrThrow<api.ContactCentreMeResponse>(response, "GET /api/contact-centre/me");
    },
  });
}

function hasCcView(me: api.ContactCentreMeResponse | null): boolean {
  return me?.capabilities.includes("cc:view") === true;
}

interface ContactCentreShellProps {
  children: ReactNode;
  companionExit?: boolean;
}

export function ContactCentreShell({ children, companionExit = false }: ContactCentreShellProps) {
  const meQuery = useContactCentreMe();
  useDocumentTitle("Contact Centre");

  if (meQuery.isPending) {
    return (
      <div className="grid min-h-dvh place-items-center bg-cream-warm">
        <div className="h-10 w-64 animate-pulse rounded-md bg-mist/50" aria-hidden />
      </div>
    );
  }

  if (meQuery.isError || !hasCcView(meQuery.data ?? null)) {
    return <ContactCentreDenied companionExit={companionExit} />;
  }

  return (
    <div className="relative min-h-dvh bg-cream-warm">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-navy focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white focus:shadow-modal focus:outline-2 focus:outline-offset-2 focus:outline-teal-400"
      >
        Skip to content
      </a>
      <ContactCentreChrome companionExit={companionExit} userId={meQuery.data?.userId ?? null} />
      <main id="main-content" tabIndex={-1} className="min-h-dvh focus:outline-none">
        {children}
      </main>
    </div>
  );
}

function ContactCentreDenied({ companionExit }: { companionExit: boolean }) {
  return (
    <div className="relative grid min-h-dvh place-items-center bg-cream-warm p-8">
      <ContactCentreChrome companionExit={companionExit} userId={null} />
      <div className="max-w-md text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-gray-500">Contact Centre</p>
        <h1 className="mt-2 font-semibold text-navy text-xl">No Contact Centre access</h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          Your account has no Contact Centre seat. If you should have one, ask your HealthOS admin
          to assign the role.
        </p>
      </div>
    </div>
  );
}
